// ============================================================================
// BP-13-02: Admin Types Index
// BrightPath Platform - Admin Frontend
// ============================================================================

// User types
export * from './users';

// Moderation types
export * from './moderation';

// Analytics types
export * from './analytics';

// =============================================================================
// Common Admin Types
// =============================================================================

import { z } from 'zod';

// Admin Resource Management
export const ResourceStatusEnum = z.enum([
  'draft',
  'published',
  'archived',
]);
export type ResourceStatus = z.infer<typeof ResourceStatusEnum>;

export const ResourceTypeEnum = z.enum([
  'article',
  'guide',
  'video',
  'checklist',
  'template',
  'external',
]);
export type ResourceType = z.infer<typeof ResourceTypeEnum>;

export const AdminResourceSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  excerpt: z.string().nullable(),
  content: z.string(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  resourceType: ResourceTypeEnum,
  status: ResourceStatusEnum,
  featuredImageUrl: z.string().nullable(),
  diagnosisIds: z.array(z.string().uuid()),
  tags: z.array(z.string()),
  viewCount: z.number(),
  saveCount: z.number(),
  avgRating: z.number().nullable(),
  ratingCount: z.number(),
  authorId: z.string().uuid(),
  authorName: z.string(),
  isPremium: z.boolean(),
  readingTimeMinutes: z.number().nullable(),
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AdminResource = z.infer<typeof AdminResourceSchema>;

export const AdminResourceListItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  slug: z.string(),
  categoryName: z.string().nullable(),
  resourceType: ResourceTypeEnum,
  status: ResourceStatusEnum,
  viewCount: z.number(),
  avgRating: z.number().nullable(),
  authorName: z.string(),
  publishedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});
export type AdminResourceListItem = z.infer<typeof AdminResourceListItemSchema>;

export const CreateResourceSchema = z.object({
  title: z.string().min(1),
  slug: z.string().optional(),
  excerpt: z.string().optional(),
  content: z.string(),
  categoryId: z.string().uuid().optional(),
  resourceType: ResourceTypeEnum,
  status: ResourceStatusEnum.default('draft'),
  featuredImageUrl: z.string().optional(),
  diagnosisIds: z.array(z.string().uuid()).default([]),
  tags: z.array(z.string()).default([]),
  isPremium: z.boolean().default(false),
  readingTimeMinutes: z.number().optional(),
});
export type CreateResource = z.infer<typeof CreateResourceSchema>;

export const UpdateResourceSchema = CreateResourceSchema.partial();
export type UpdateResource = z.infer<typeof UpdateResourceSchema>;

export const ResourceListParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  search: z.string().optional(),
  categoryId: z.string().uuid().optional(),
  resourceType: ResourceTypeEnum.optional(),
  status: ResourceStatusEnum.optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'title', 'viewCount']).default('updatedAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type ResourceListParams = z.infer<typeof ResourceListParamsSchema>;

export const ResourceListResponseSchema = z.object({
  items: z.array(AdminResourceListItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type ResourceListResponse = z.infer<typeof ResourceListResponseSchema>;

// Resource Categories
export const ResourceCategorySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  displayOrder: z.number(),
  parentId: z.string().uuid().nullable(),
  resourceCount: z.number(),
});
export type ResourceCategory = z.infer<typeof ResourceCategorySchema>;

// Quick Action Types
export const QuickActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  icon: z.string(),
  href: z.string().optional(),
  onClick: z.function().optional(),
  badge: z.number().optional(),
  variant: z.enum(['default', 'warning', 'danger']).default('default'),
});
export type QuickAction = z.infer<typeof QuickActionSchema>;

// Admin Navigation
export interface AdminNavItem {
  id: string;
  label: string;
  href: string;
  icon: string;
  badge?: number;
  badgeVariant?: 'default' | 'warning' | 'danger';
  children?: AdminNavItem[];
}

// Admin Filters State
export interface AdminFiltersState {
  search: string;
  dateFrom: string | null;
  dateTo: string | null;
  status: string | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}
